import { Response } from "node-fetch";
import fetchWithProxy from "@foundation/util/lib/fetchWithProxy";

import { VALID_REGEX } from "../../util/constants";
import { stringifyParams } from "../../util/stringifyParams";
import Singleton from "../Singleton";


import { SrchDataClientError, SrchDataErrorResponse } from "./Error";
import {
  SrchDataBio,
  SrchDataCompanyPrivate,
  SrchDataCompanyPublic,
  SrchDataGetBioParams,
  SrchDataGetCompaniesPrivateParams,
  SrchDataGetCompaniesPublicParams,
  SrchDataGetSecuritiesParams,
  SrchDataSecurity
} from "./types";

export type SrchDataClientParams = {
  host: string;
  proxy?: string;
};

export default class SrchDataClient extends Singleton {
  host: string;
  fetchWithProxy: (url: string) => Promise<Response>;

  constructor(params: SrchDataClientParams) {
    super();

    if (!params.host) {
      throw new Error(
        `No host passed to SrchDataClient constructor; aborting`
      );
    }

    this.host = `${params.host}/srchdata`;
    this.fetchWithProxy = (url) => fetchWithProxy(url, params.proxy);
  }

  async getBio(params: SrchDataGetBioParams): Promise<SrchDataBio[]> {
    this.validateGetBio(params);

    const urlParams = stringifyParams(params);
    const url = `${this.host}/bio?${urlParams}`;

    const response = await this.fetchWithProxy(url);
    const data = await response.json();

    if (!response.ok) {
      throw new SrchDataClientError({
        errorResponse: data as SrchDataErrorResponse,
        status: response.status,
        url,
      });
    }

    return data.bio;
  }

  async getTotalNumberOfBios(params: SrchDataGetBioParams): Promise<number> {
    this.validateGetBio(params);

    const urlParams = stringifyParams(params);
    const url = `${this.host}/bio?${urlParams}`;

    const response = await this.fetchWithProxy(url);
    const data = await response.json();

    if (!response.ok) {
      throw new SrchDataClientError({
        errorResponse: data as SrchDataErrorResponse,
        status: response.status,
        url,
      });
    }

    return data.total;
  }

  async getCompaniesPublic(params: SrchDataGetCompaniesPublicParams): Promise<SrchDataCompanyPublic[]> {
    this.validateGetCompaniesPublic(params);

    const urlParams = stringifyParams(params);
    const url = `${this.host}/search/companies/public/diff?${urlParams}`;

    const response = await this.fetchWithProxy(url);
    const data = await response.json();

    if (!response.ok) {
      throw new SrchDataClientError({
        errorResponse: data as SrchDataErrorResponse,
        status: response.status,
        url,
      });
    }

    return data.value.hits;
  }

  async getNumberOfPublicCompanies(): Promise<number> {
    const url = `${this.host}/search/companies/public/diff`;

    const response = await this.fetchWithProxy(url);
    const data = await response.json();

    if (!response.ok) {
      throw new SrchDataClientError({
        errorResponse: data as SrchDataErrorResponse,
        status: response.status,
        url,
      });
    }

    return data.value.totalHits;
  }

  async getCompaniesPrivate(params: SrchDataGetCompaniesPrivateParams): Promise<SrchDataCompanyPrivate[]> {
    this.validateGetCompaniesPrivate(params);

    const urlParams = stringifyParams(params);
    const url = `${this.host}/search/companies/private/diff?${urlParams}`;


    const response = await this.fetchWithProxy(url);
    const data = await response.json();

    if (!response.ok) {
      throw new SrchDataClientError({
        errorResponse: data as SrchDataErrorResponse,
        status: response.status,
        url,
      });
    }

    return data.value.hits;
  }

  async getNumberOfPrivateCompanies(): Promise<number> {

    const url = `${this.host}/search/companies/private/diff`;

    const response = await this.fetchWithProxy(url);
    const data = await response.json();

    if (!response.ok) {
      throw new SrchDataClientError({
        errorResponse: data as SrchDataErrorResponse,
        status: response.status,
        url,
      });
    }

    return data.value.totalHits;
  }

  async getSecurities(params: SrchDataGetSecuritiesParams): Promise<SrchDataSecurity[]> {
    this.validateGetSecurities(params);

    const urlParams = stringifyParams(params);
    const url = `${this.host}/search/securities/diff?${urlParams}`;

    const response = await this.fetchWithProxy(url);
    const data = await response.json();

    if (!response.ok) {
      throw new SrchDataClientError({
        errorResponse: data as SrchDataErrorResponse,
        status: response.status,
        url,
      });
    }

    return data.value.hits;
  }

  validateGetBio = (params: SrchDataGetBioParams): boolean => {
    if (params.offset) {
      if (params.offset < 0 || typeof params.offset !== "number") {
        throw new Error(
          `Invalid offset passed to SrchDatClient.getBio: ${params.offset} of type ${typeof params.offset}`
        );
      }
    }

    if (params.size) {
      if (params.size < 0 || typeof params.size !== "number") {
        throw new Error(
          `Invalid size passed to SrchDatClient.getBio: ${params.size} of type ${typeof params.size}`
        );
      }

      if (params.size > 1000) {
        throw new Error(
          `Invalid size passed to SrchDatClient.getBio: ${params.size} exceeds size limit of 1000`
        );
      }
    }

    if (params.start && (params.start < 0 || typeof params.start !== "number")) {
      throw new Error(
        `Invalid start passed to SrchDatClient.getBio: ${params.start} of type ${typeof params.start}`
      );
    }

    if (params.isDeleted  && typeof params.isDeleted !== "boolean") {
      throw new Error(
        `Invalid isDeleted passed to SrchDatClient.getBio: ${params.isDeleted} of type ${typeof params.isDeleted}`
      );
    }

    if (params.personId  && (params.personId < 0 || typeof params.personId !== "number")) {
      throw new Error(
        `Invalid personId passed to SrchDatClient.getBio: ${params.personId} of type ${typeof params.personId}`
      );
    }

    return true;
  };

  validateGetCompaniesPublic = (params: SrchDataGetCompaniesPublicParams): boolean => {
    if (params.startTime && (typeof params.startTime !== "string" || !params.startTime.match(VALID_REGEX.dateString))) {
      throw new Error(
        `Invalid startTime passed to SrchDatClient.getCompaniesPublic: ${params.startTime} of type ${typeof params.startTime}`
      );
    }

    if (params.endTime && (typeof params.endTime !== "string" || !params.endTime.match(VALID_REGEX.dateString))) {
      throw new Error(
        `Invalid endTime passed to SrchDatClient.getCompaniesPublic: ${params.endTime} of type ${typeof params.endTime}`
      );
    }

    if (params.from && (params.from < 0 || typeof params.from !== "number")) {
      throw new Error(
        `Invalid from passed to SrchDatClient.getCompaniesPublic: ${params.from} of type ${typeof params.from}`
      );
    }

    if (params.size) {
      if (params.size < 0 || typeof params.size !== "number") {
        throw new Error(
          `Invalid size passed to SrchDatClient.getCompaniesPublic: ${params.size} of type ${typeof params.size}`
        );
      }

      if (params.size > 1000) {
        throw new Error(
          `Invalid size passed to SrchDatClient.getCompaniesPublic: ${params.size} exceeds size limit of 1000`
        );
      }
    }

    return true;
  };

  validateGetCompaniesPrivate = (params: SrchDataGetCompaniesPrivateParams): boolean => {
    if (params.startTime && (typeof params.startTime !== "string" || !params.startTime.match(VALID_REGEX.dateString))) {
      throw new Error(
        `Invalid startTime passed to SrchDatClient.getCompaniesPrivate: ${params.startTime} of type ${typeof params.startTime}`
      );
    }

    if (params.endTime && (typeof params.endTime !== "string" || !params.endTime.match(VALID_REGEX.dateString))) {
      throw new Error(
        `Invalid endTime passed to SrchDatClient.getCompaniesPrivate: ${params.endTime} of type ${typeof params.endTime}`
      );
    }

    if (params.from && (params.from < 0 || typeof params.from !== "number")) {
      throw new Error(
        `Invalid from passed to SrchDatClient.getCompaniesPrivate: ${params.from} of type ${typeof params.from}`
      );
    }

    if (params.size) {
      if (params.size < 0 || typeof params.size !== "number") {
        throw new Error(
          `Invalid size passed to SrchDatClient.getCompaniesPrivate: ${params.size} of type ${typeof params.size}`
        );
      }

      if (params.size > 1000) {
        throw new Error(
          `Invalid size passed to SrchDatClient.getCompaniesPrivate: ${params.size} exceeds size limit of 1000`
        );
      }
    }

    return true;
  };

  validateGetSecurities = (params: SrchDataGetSecuritiesParams): boolean => {
    if (params.startTime && (typeof params.startTime !== "string" || !params.startTime.match(VALID_REGEX.dateString))) {
      throw new Error(
        `Invalid startTime passed to SrchDatClient.getSecurities: ${params.startTime} of type ${typeof params.startTime}`
      );
    }

    if (params.endTime && (typeof params.endTime !== "string" || !params.endTime.match(VALID_REGEX.dateString))) {
      throw new Error(
        `Invalid endTime passed to SrchDatClient.getSecurities: ${params.endTime} of type ${typeof params.endTime}`
      );
    }

    if (params.from && (params.from < 0 || typeof params.from !== "number")) {
      throw new Error(
        `Invalid from passed to SrchDatClient.getSecurities: ${params.from} of type ${typeof params.from}`
      );
    }

    if (params.size) {
      if (params.size < 0 || typeof params.size !== "number") {
        throw new Error(
          `Invalid size passed to SrchDatClient.getSecurities: ${params.size} of type ${typeof params.size}`
        );
      }

      if (params.size > 1000) {
        throw new Error(
          `Invalid size passed to SrchDatClient.getSecurities: ${params.size} exceeds size limit of 1000`
        );
      }
    }

    return true;
  };
}
